import {computed, observable, makeObservable, runInAction} from "mobx";
import type {AppState} from "../state/AppState";
import {XyiconFeature} from "../../generated/api/base";
import type {SpaceDto, SpaceFileDto, SpaceUpdateFieldDto} from "../../generated/api/base";
import {DateUtils} from "../../utils/DateUtils";
import type {OrgSearchResponseDto} from "../../generated/api/search";
import type {IModel} from "./Model";
import type {SpaceVersion} from "./SpaceVersion";
import {SpaceFile} from "./SpaceFile";

export class Space implements IModel, Omit<SpaceDto, "spaceFiles"> {
	public static search(space: Space, search = "") {
		if (!search) {
			return true;
		}

		search = search.toLowerCase();
		const refId = space.refId.toLowerCase();

		if (refId.indexOf(search) > -1) {
			return true;
		}

		return false;
	}

	public readonly ownFeature = XyiconFeature.Space;

	private _data: SpaceDto;

	private _spaceFiles: SpaceFile[] = [];

	private _selectedSpaceFileId: string;

	private readonly _appState: AppState;

	constructor(data: SpaceDto, appState: AppState) {
		makeObservable<this, "_data" | "_spaceFiles" | "_selectedSpaceFileId">(this, {
			_data: observable,
			_spaceFiles: observable,
			_selectedSpaceFileId: observable,
			selectedSpaceFile: computed,
			id: computed,
			refId: computed,
			typeId: computed,
			type: computed,
			typeName: computed,
			portfolioId: computed,
			name: computed,
			spaceUnitsPerMeter: computed,
			newestSpaceFile: computed,
			newestValidSpaceFile: computed,
			thumbnailFileURL: computed,
			newestSourceFileURL: computed,
			spaceFiles: computed,
			versionId: computed,
			versions: computed,
			issuanceDate: computed,
			versionName: computed,
			fieldData: computed,
			lastModifiedBy: computed,
			lastModifiedAt: computed,
			data: computed,
		});
		this._appState = appState;
		this.applyData(data);
	}

	public applyUpdate(data: SpaceUpdateFieldDto) {
		if (data.spaceIDList.includes(this.id)) {
			this.applyFieldValues(data.fieldValues);
			this._data.lastModifiedAt = data.lastModifiedAt || this._data.lastModifiedAt;
			this._data.lastModifiedBy = data.lastModifiedBy || this._data.lastModifiedBy;
		}
	}

	public applyFieldValues(fieldValues: Record<string, any> | null) {
		if (fieldValues?.name !== undefined) {
			this.name = fieldValues.name;
		}
	}

	public applyData(data: SpaceDto) {
		runInAction(() => {
			this._data = data;
			this._spaceFiles = (this._data.spaceFiles ?? [])
				.map((spaceFileData: SpaceFileDto) => new SpaceFile(spaceFileData, this, this._appState))
				.toSorted(this.sortSpaceFilesByIssueanceDate);

			this._selectedSpaceFileId = this.newestValidSpaceFile?.id;
			this._data.fieldData = this._data.fieldData || {};
		});
	}

	private readonly sortSpaceFilesByIssueanceDate = (a: SpaceFile, b: SpaceFile) => DateUtils.sortByDate(a.spaceVersion?.date, b.spaceVersion?.date);

	public removeTemporarySpaceFiles() {
		this._spaceFiles = this._spaceFiles.filter((spaceFile: SpaceFile) => spaceFile.id != null);
	}

	// Newest is the [0] element, oldest is the [n-1]
	// Temporary space files are generated while the user is creating / aligning a new spacefile
	public addTemporarySpaceFile(spaceFileData: SpaceFileDto) {
		const newSpaceFile = new SpaceFile(spaceFileData, this, this._appState);

		// Override, if a temporary spacefile already exists
		if (this.newestSpaceFile.id == null) {
			this._spaceFiles[0] = newSpaceFile;
		} else {
			this._spaceFiles.unshift(newSpaceFile);
		}
	}

	public addSpaceFile(spaceFileData: SpaceFileDto) {
		const newSpaceFile = new SpaceFile(spaceFileData, this, this._appState);

		this._spaceFiles.unshift(newSpaceFile);
		// The newly added spacefile might not be the newest, when it comes to issueance date of spaceversions
		// So we need to sort it again
		this._spaceFiles = this._spaceFiles.slice().sort(this.sortSpaceFilesByIssueanceDate);
		this.setSelectedSpaceFile(this.newestSpaceFile);
	}

	public setSelectedSpaceFile(spaceFile: SpaceFile) {
		this._selectedSpaceFileId = spaceFile?.id || "";
	}

	public setSpaceUnitsPerMeter(spaceUnitsPerMeter: number) {
		this._data.unitsPerMeter = spaceUnitsPerMeter;
	}

	public setSpaceType(typeID: string) {
		this._data.spaceTypeID = typeID;
	}

	public get selectedSpaceFile() {
		return this._spaceFiles.find((spaceFile) => spaceFile.id === this._selectedSpaceFileId) || this.newestSpaceFile;
	}

	public get id() {
		return this._data.spaceID;
	}

	public get refId() {
		return this._data.spaceRefID;
	}

	public get typeId() {
		return this._data.spaceTypeID;
	}

	public get type() {
		return this._appState.actions.getTypeById(this.typeId);
	}

	public get typeName() {
		return this.type?.name || "";
	}

	public get portfolioId() {
		return this._data.portfolioID;
	}

	public set name(value: string) {
		this._data.name = value;
	}

	public get name() {
		return this._data.name ?? "";
	}

	public get spaceUnitsPerMeter() {
		return this._data.unitsPerMeter;
	}

	public get newestSpaceFile() {
		return this._spaceFiles[0];
	}

	public get newestValidSpaceFile() {
		return this._spaceFiles.find((spaceFile: SpaceFile) => spaceFile.id != null);
	}

	public get thumbnailFileURL() {
		return this.selectedSpaceFile.thumbnailFileURL;
	}

	public get newestSourceFileURL() {
		return this.newestSpaceFile.sourceFileURL;
	}

	public get spaceFiles() {
		return this._spaceFiles;
	}

	public get versionId() {
		return (this.selectedSpaceFile || this.newestSpaceFile).spaceVersionId;
	}

	public get versions() {
		return this.spaceFiles.map((spaceFile: SpaceFile) =>
			this._appState.actions.getFeatureItemById(spaceFile.spaceVersionId, XyiconFeature.SpaceVersion),
		) as SpaceVersion[];
	}

	public get issuanceDate() {
		return this._appState.actions.getFeatureItemById<SpaceVersion>(this.versionId, XyiconFeature.SpaceVersion)?.date || "";
	}

	public get versionName() {
		// this is used by DetailsContent
		return (
			this.selectedSpaceFile?.spaceVersionName ??
			this._appState.actions.getFeatureItemById<SpaceVersion>(this.versionId, XyiconFeature.SpaceVersion)?.name ??
			""
		);
	}

	public get fieldData() {
		return this._data.fieldData;
	}

	public get linkedFieldData() {
		return (this._data as OrgSearchResponseDto).linkedFieldData ?? {};
	}

	public get lastModifiedBy() {
		return this._data.lastModifiedBy;
	}

	public get lastModifiedAt() {
		return this._data.lastModifiedAt;
	}

	public get data() {
		return this._data;
	}
}
